import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import { SUPPLIER_CACHE_KEY } from "./keys";

import {
  CreateSupplier,
  CreateSupplierClosure,
  UpdateSupplier,
  UpdateSupplierClosure,
} from "@/types/supplier";
import { SupplierRepository } from "@/repositories/supplier";

export const useGetAllSuppliers = () => {
  const supplierRepository = useRef(new SupplierRepository());

  return useQuery({
    queryKey: SUPPLIER_CACHE_KEY.LIST,
    queryFn: () => supplierRepository.current.findAll(),
  });
};

export const useCreateSupplier = () => {
  const supplierRepository = useRef(new SupplierRepository());
  const { refetch } = useGetAllSuppliers();

  return useMutation({
    mutationFn: async (data: CreateSupplier) => {
      return supplierRepository.current.create(data);
    },
    onSuccess: () => {
      refetch();
    },
  });
};

export const useUpdateSupplier = () => {
  const supplierRepository = useRef(new SupplierRepository());
  const { refetch } = useGetAllSuppliers();

  return useMutation({
    mutationFn: async (data: UpdateSupplier) => {
      return supplierRepository.current.update(data.id, data);
    },
    onSuccess: () => {
      refetch();
    },
  });
};

export const useDeleteSupplier = () => {
  const supplierRepository = useRef(new SupplierRepository());
  const { refetch } = useGetAllSuppliers();

  return useMutation({
    mutationFn: async (data: { id: string }) => {
      return supplierRepository.current.delete(data.id);
    },
    onSuccess: () => {
      refetch();
    },
  });
};

export const useGetSupplierClosures = (supplierId: string) => {
  const supplierRepository = useRef(new SupplierRepository());

  return useQuery({
    queryKey: SUPPLIER_CACHE_KEY.CLOSURES(supplierId),
    queryFn: () => supplierRepository.current.findClosures(supplierId),
  });
};

export const useCreateSupplierClosure = (supplierId: string) => {
  const queryClient = useQueryClient();
  const supplierRepository = useRef(new SupplierRepository());

  return useMutation({
    mutationFn: (data: CreateSupplierClosure) =>
      supplierRepository.current.createClosure(supplierId, data),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: SUPPLIER_CACHE_KEY.CLOSURES(supplierId),
      }),
  });
};

export const useUpdateSupplierClosure = (supplierId: string) => {
  const queryClient = useQueryClient();
  const supplierRepository = useRef(new SupplierRepository());

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSupplierClosure }) =>
      supplierRepository.current.updateClosure(supplierId, id, data),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: SUPPLIER_CACHE_KEY.CLOSURES(supplierId),
      }),
  });
};

export const useDeleteSupplierClosure = (supplierId: string) => {
  const queryClient = useQueryClient();
  const supplierRepository = useRef(new SupplierRepository());

  return useMutation({
    mutationFn: (closureId: string) =>
      supplierRepository.current.deleteClosure(supplierId, closureId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: SUPPLIER_CACHE_KEY.CLOSURES(supplierId),
      }),
  });
};
